import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import readXlsxFile from 'read-excel-file';
import { getBusinessContacts } from 'store/projects/services/getBusinessContacts';
import { getBusinessIdPartnerConfiguration } from 'store/projects/services/getBusinessIdPartnerConfiguration';
import { getEnabledIntegrationPartnersByBusinessId } from 'store/projects/services/getEnabledIntegrationPartnersByBusinessId';
import { getProjectIlveability } from 'store/projects/services/getProjectIlveability';
import { selectProjectResources } from 'store/resources/selectors/resources.selectors';
import { loadResourcesOfProject } from 'store/resources/thunks/loadResourcesOfProject';
import uuidv4 from 'uuid/v4';
import writeXlsxFile from 'write-excel-file';

const EMPTY_RESOURCES = [];

/**
 * Foremen first. A project's vastaava työnjohtaja is normally one of these two
 * trades — see `constants/trades.js`, where 'VTJ' is Vastaava työnjohtaja and
 * 'TJ' is Työnjohtaja. Everyone else stays in the list, just further down: the
 * trade is recorded per (user, employer) and is often left unset.
 */
const SITE_MANAGER_TRADE_RANK = { VTJ: 0, TJ: 1 };
const rankByTrade = (resource) =>
  SITE_MANAGER_TRADE_RANK[resource?.trade] ?? 2;

function useProjectDialog(businessId, projectId) {
  const [contactOptions, setContactOptions] = useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const projectResources = useSelector((state) =>
    projectId ? selectProjectResources(state, projectId) : EMPTY_RESOURCES
  );

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const result = await getBusinessContacts(businessId);
        setContactOptions(result);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setContactOptions([]);
      }
    };

    fetchSuggestions();
  }, [businessId]);

  /**
   * The site manager picker suggests people already on the project rather than
   * names typed on other projects. That is not just convenience: the
   * scheduled-forms lambda resolves a project's site manager to a real user by
   * matching the stored phone number against the project's resources, and drops
   * anyone who fails to match — so a site manager picked here carries a phone
   * that resolves by construction.
   */
  useEffect(() => {
    if (!projectId) {
      return;
    }
    dispatch(loadResourcesOfProject(projectId));
  }, [dispatch, projectId]);

  const siteManagerOptions = useMemo(() => {
    const seen = new Set();

    return projectResources
      .filter(
        (resource) =>
          !resource?.deletedAt && (resource?.firstName || resource?.lastName)
      )
      .sort((a, b) => rankByTrade(a) - rankByTrade(b))
      .reduce((options, resource) => {
        // The same person can hold a resource per employer; one row each would
        // just look like duplicates in the dropdown.
        const key = `${resource.firstName} ${resource.lastName} ${resource.phone || ''}`;
        if (seen.has(key)) {
          return options;
        }
        seen.add(key);

        options.push({
          site_manager_first_name: resource.firstName || '',
          site_manager_last_name: resource.lastName || '',
          site_manager_email: resource.email || '',
          site_manager_phone: resource.phone || '',
          // Kept so the backend can tell whether the signed-in user IS this
          // project's vastaava työnjohtaja, which is what grants them the right
          // to sign that slot on its worksite journals. Without it the match
          // falls back to comparing phone numbers, which is a guess where this
          // is an identity.
          site_manager_user_id: resource.user || null,
        });
        return options;
      }, []);
  }, [projectResources]);

  const getIlveability = async (parentId, projectId) => {
    try {
      const result = await getProjectIlveability(parentId, projectId);
      return result;
    } catch (error) {
      console.error('Error fetching ilveability:', error);
      enqueueSnackbar(t('project_dialog.snackbar_ilveability_failed'), {
        variant: 'error',
      });
      return false;
    }
  };

  const getIntegrationPartners = async () => {
    try {
      const results = [];
      const IntegrationPartnerResult =
        await getEnabledIntegrationPartnersByBusinessId(businessId);
      if (IntegrationPartnerResult && IntegrationPartnerResult.length > 0) {
        // console.log('Integration partner:', IntegrationPartnerResult);
        results.push(...IntegrationPartnerResult);
      }
      const partnerConfigurationResult =
        await getBusinessIdPartnerConfiguration(businessId);
      if (partnerConfigurationResult) {
        // console.log('Business ID Partner Configuration:', partnerConfigurationResult);
        results.push(partnerConfigurationResult);
      }
      return results;
    } catch (error) {
      console.error('Error fetching integration partner:', error);
      enqueueSnackbar(t('project_dialog.snackbar_integration_partner_failed'), {
        variant: 'error',
      });
      return [];
    }
  };

  const readAssignmentsFromXLS = async (file) => {
    console.log('Reading assignments from XLS');
    try {
      let rows = await readXlsxFile(file);
      const assignments = [];
      const lastAtDepth = {};
      rows.shift();
      console.log('Rows:', rows);

      rows.forEach((row) => {
        const depth = row.findIndex((item) => item !== null); // Depth is where the first non-null number is found
        const number = row[depth]; // This is the assignment number at this depth
        const name = row[depth + 1]; // Next item is the assignment name
        const unitAmountGoal = row[depth + 2]; // Next item is the unit amount goal
        const unit = row[depth + 3]; // Next item is the unit
        const unitPrice = row[depth + 4]; // Next item is the unit price

        const assignment = {
          id: uuidv4(), // Generate a unique ID
          number: number?.toString(),
          name: name?.toString() || '',
          unitAmountGoal: unitAmountGoal?.toString() || '',
          unit: unit?.toString() || '',
          unitPrice: unitPrice?.toString() || '',
          parent: null,
        };

        // If there's a parent at the level above, set it as the parent
        if (depth > 0 && lastAtDepth[depth - 1]) {
          assignment.parent = lastAtDepth[depth - 1].id;
        }

        assignments.push(assignment);
        lastAtDepth[depth] = assignment; // Update the last assignment at this depth
      });
      console.log('lastAtDepth', lastAtDepth);
      console.log('Created assignments:', assignments);
      return assignments;
    } catch (error) {
      console.error('Error reading assignments from XLS:', error);
      enqueueSnackbar('Virhe luettaessa litteroita tiedostosta', {
        variant: 'error',
      });
      return [];
    }
  };

  const writeAssignmentsToXLS = async (assignments, projectName) => {
    const childrenMap = new Map();

    assignments.forEach((assignment) => {
      childrenMap.set(assignment.id, []);
    });

    assignments.forEach((assignment) => {
      if (assignment.parent) {
        const parentChildren = childrenMap.get(assignment.parent);
        if (parentChildren) {
          parentChildren.push(assignment.id);
        }
      }
    });

    function buildRows(assignmentId, depth) {
      const assignment = assignments.find((a) => a.id === assignmentId);
      const row = Array(maxDepth + 1).fill(null);

      row[depth] = { value: assignment.number?.toString() || '' };
      row[depth + 1] = { value: assignment.name?.toString() || '' };
      row[depth + 2] = {
        value: assignment.unitAmountGoal?.toString() || '',
      };
      row[depth + 3] = { value: assignment.unit?.toString() || '' };
      row[depth + 4] = { value: assignment.unitPrice?.toString() || '' };

      data.push(row);

      const children = childrenMap.get(assignmentId) || [];
      children
        .sort((a, b) => a.number - b.number)
        .forEach((childId) => buildRows(childId, depth + 1));
    }

    const maxDepth = Math.max(
      ...assignments.map((assignment) => {
        let depth = 0;
        let parent = assignment.parent;
        while (parent) {
          depth++;
          parent = assignments.find((a) => a.id === parent)?.parent;
        }
        return depth + 1;
      })
    );

    const data = [];

    assignments
      .sort((a, b) => a.number - b.number)
      .forEach((assignment) => {
        if (!assignment.parent) {
          buildRows(assignment.id, 0);
        }
      });

    const HEADER_ROW = [
      {
        value: t('project_dialog.assignment_number'),
        fontWeight: 'bold',
      },
      {
        value: t('project_dialog.assignment_name'),
        fontWeight: 'bold',
      },
      {
        value: t('project_dialog.assignment_amount'),
        fontWeight: 'bold',
      },
      {
        value: t('project_dialog.assignment_unit'),
        fontWeight: 'bold',
      },
      {
        value: t('project_dialog.assignment_unit_price'),
        fontWeight: 'bold',
      },
    ];

    const rows = [HEADER_ROW, ...data];

    console.log('Writing assignments to XLS:', rows);

    await writeXlsxFile(rows, {
      fileName: `${projectName.replace(/\s+/g, '_')}_litterat.xlsx`,
      stickyRowsCount: 1,
    });
  };

  return {
    contactOptions,
    siteManagerOptions,
    getIlveability,
    getIntegrationPartners,
    readAssignmentsFromXLS,
    writeAssignmentsToXLS,
  };
}

export default useProjectDialog;
